import { FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import ModalForm from "../../../components/ModalForm";
import {
    postNoTradesSession,
    putNoTradesSession,
} from "../../../services/api/noTradesSession";
import { Timeslots } from "../../../types/enums/Timeslots";
import { NoTradesSession } from "../../../types/NoTradesSession";
import {
    getFormattedDateInput,
    isWeekendInputDate,
    parseInputDateToLocal,
} from "../../../utils/date";
import { getOptionsFromEnum } from "../../../utils/form";

import { AuthContext } from "../../../contexts/AuthContext";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";
import { isAdminRole } from "../../../utils/graduation";
import styles from "./styles.module.scss";

interface Props {
    mode: "add" | "edit";
    open: boolean;
    handleClose: () => void;
    session?: NoTradesSession;
    /** En mode « add », préremplit la date (YYYY-MM-DD) */
    initialDateForAdd?: string;
}

const NoTradeModal: FC<Props> = ({
    mode,
    open,
    handleClose,
    session,
    initialDateForAdd,
}) => {
    const { user, getUserIdFromQuery } = useContext(AuthContext)!;
    const userId = getUserIdFromQuery()!;

    const dateNow = new Date();
    const adjustedDate = isWeekendInputDate(dateNow)
        ? new Date(
              dateNow.getTime() -
                  (dateNow.getDay() === 0 ? 2 : 1) * 24 * 60 * 60 * 1000
          )
        : dateNow;
    const defaultTime = "";
    const timeslotOptions = [
        { value: "", label: "Sélectionner un créneau" },
        ...getOptionsFromEnum(Timeslots),
    ];

    const [date, setDate] = useState<Date>(adjustedDate);
    const [lastValidDate, setLastValidDate] = useState<Date>(dateNow);
    const [timeslot, setTimeslot] = useState<string>(defaultTime);
    const [comments, setComments] = useState("");
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    const handleDateChange = (value: Date) => {
        setDate(value);
        setFieldErrorsState((prev) => clearFieldError(prev, "date"));
    };

    const handleDateBlur = () => {
        if (!date) {
            setDate(lastValidDate);
            return;
        }

        if (isWeekendInputDate(date)) {
            toast.warning(
                "Les sessions de trading ne peuvent pas être ajoutées le week-end"
            );
            setDate(lastValidDate);
            return;
        }

        setLastValidDate(date);
    };

    const handleAddTrade = async () => {
        const nextErrors = setFieldErrors(
            ["date", !date && REQUIRED_FIELD],
            ["timeslot", !timeslot && REQUIRED_FIELD]
        );
        setFieldErrorsState(nextErrors);
        if (
            notifyFieldErrors(nextErrors, {
                message: "Veuillez remplir tous les champs obligatoires",
                type: "warning",
            })
        ) {
            return;
        }

        if (isWeekendInputDate(date)) {
            toast.warning(
                "Les sessions de trading ne peuvent pas être ajoutées le week-end"
            );
            return;
        }

        const noTradingSessionParams = {
            date: getFormattedDateInput(date),
            timeslot,
            comment: comments || undefined,
        };

        try {
            if (mode === "edit" && session) {
                await putNoTradesSession(
                    userId,
                    session.id,
                    noTradingSessionParams
                );
            } else {
                await postNoTradesSession(userId, noTradingSessionParams);
            }

            handleClose();
            toast.success(
                `No trade ${mode === "add" ? "ajouté" : "modifié"} avec succès`
            );
        } catch (error) {}
    };

    // Si on ferme la modal, on remet les valeurs des inputs à leur valeur initiale
    useEffect(() => {
        if (!open) {
            const now = new Date();
            const adjusted = isWeekendInputDate(now)
                ? new Date(
                      now.getTime() -
                          (now.getDay() === 0 ? 2 : 1) * 24 * 60 * 60 * 1000
                  )
                : now;
            setDate(adjusted);
            setLastValidDate(now);
            setTimeslot(defaultTime);
            setComments("");
            setFieldErrorsState({});
        }
    }, [open, defaultTime]);

    // Si on reçoit une session en props, on met à jour les valeurs des inputs
    useEffect(() => {
        if (session) {
            setDate(new Date(session.date));
            if (!isWeekendInputDate(session.date)) {
                setLastValidDate(new Date(session.date));
            }
            setTimeslot(session.timeslot);
            setComments(session.comment || "");
        }
    }, [session]);

    useEffect(() => {
        if (!open || mode !== "add" || !initialDateForAdd || session) return;
        const parsed = parseInputDateToLocal(initialDateForAdd);
        if (!parsed || isWeekendInputDate(parsed)) return;
        setDate(parsed);
        setLastValidDate(parsed);
    }, [open, mode, initialDateForAdd, session]);

    return (
        <ModalForm
            open={open}
            handleClose={handleClose}
            size="small"
            title={`${mode === "add" ? "Ajouter" : "Modifier"} un no trade`}
            onSubmit={handleAddTrade}
            contentClassName={styles.modalContent}
            fields={[
                {
                    id: "date",
                    label: "Date",
                    type: "date",
                    value: date,
                    onChange: (value) => handleDateChange(value as Date),
                    onBlur: handleDateBlur,
                    min:
                        !isAdminRole(user?.role)
                            ? new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)
                            : undefined,
                    max: new Date(),
                    error: fieldErrors.date,
                },
                {
                    id: "timeslot",
                    label: "Créneau horaire",
                    type: "select",
                    options: timeslotOptions,
                    value: timeslot,
                    onChange: (value) => {
                        setTimeslot(value as string);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "timeslot")
                        );
                    },
                    error: fieldErrors.timeslot,
                },
                {
                    id: "comment",
                    label: "Commentaires",
                    value: comments,
                    onChange: (value) => setComments(value as string),
                    type: "textarea",
                    max: 2500,
                },
            ]}
            submitLabel={mode === "add" ? "Ajouter" : "Modifier"}
        />
    );
};

export default NoTradeModal;
